import { Fragment, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Outlet, Link } from 'react-router-dom'
import { Bars3Icon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { boolean } from 'boolean'

import { useAuth } from 'hooks/useAuth'
import { usePageTracking } from 'hooks/use-page-tracking'
import { useStore } from 'hooks/useStore'
import { useNotificationWebSocket } from 'hooks/useNotificationWebSocket'
import { usePendingCheckoutRecovery } from 'hooks/use-pending-checkout-recovery'
import { NotificationsProvider, useNotificationsContext } from 'contexts/notifications-context'
import { Logo, IconSvg } from 'components/common'
import { MainSidebar } from 'components/organisms/main-sidebar'
import { MainSidebarMobile } from 'components/organisms/main-sidebar-mobile'
import {
	SearchBar,
	ProfileDropdown,
	NotificationDropdown,
	TrendingPiesBlock,
	UpdateTermsOfServiceAndPrivacyModal,
	DeleteAccountBlock,
	DidYouKnowBlock,
} from 'containers/common'
import { ComponentGuard } from 'guards'
import CreateActionDropdown from 'containers/common/CreateActionDropdown'
import TopBarActiveJobs from 'containers/common/TopBarActiveJobs'
import { ImpersonatedBadge } from 'components/molecules/impersonated-badge'
import { FooterLinks } from 'components/molecules/footer-links'

export function MainLayout() {
	return (
		<NotificationsProvider>
			<MainLayoutInner />
		</NotificationsProvider>
	)
}

function MainLayoutInner() {
	usePageTracking()
	usePendingCheckoutRecovery()
	const { token, authInfo } = useAuth()
	const { setLatestWsNotification } = useNotificationsContext()
	useNotificationWebSocket({
		skip: !token || !authInfo,
		onUpdate: (notification) => {
			if (notification) setLatestWsNotification(notification)
		},
	})
	const {
		header,
		hasBackArrow,
		backTo,
		blur,
		loadingLogout,
		openDeleteAccount,
		setOpenDeleteAccount,
		refetchUser,
		refetchMyEvents,
		refetchMyClubs,
		selectedEvent: _selectedEvent,
		user: _user,
	} = useStore()
	const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)

	useEffect(() => {
		if (token && authInfo && !loadingLogout) {
			const { isDeleted, isComplete } = authInfo
			refetchUser()
			if (!boolean(isDeleted) && boolean(isComplete)) {
				refetchMyEvents()
				refetchMyClubs()
			}
		}
	}, [token, JSON.stringify(authInfo), loadingLogout])

	return (
		<div className={clsx({ blur })}>
			<Helmet
				bodyAttributes={{
					class: clsx(
						'bg-theme-surface'
						// , {
						// 	'sm:overflow-visible overflow-hidden':
						// 		isAllowComponent({
						// 			excludeRoutes: ['/reward/:id', '/rewards/:id', '/pies/:id'],
						// 		}) && isIOS,
						// }
					),
				}}
			/>
			<ImpersonatedBadge />
			<div className="flex min-h-screen max-w-[1440px] mx-auto">
				<MainSidebarMobile open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
				<MainSidebar />
				<div className="flex flex-col min-w-0 flex-1 lg:pl-[232px]">
					<div className="h-[72px]">
						<div className="min-[1440px]:w-[1440px] w-full fixed top-0 left-1/2 -translate-x-1/2 z-10">
							<div className="lg:flex-none flex justify-between lg:w-[calc(100%-232px)] w-full absolute right-0 top-0 bg-theme-surface h-[72px]">
								<div className="lg:hidden">
									<div className="flex items-center sm:px-8 py-4 space-x-4 px-4">
										<div>
											<button
												type="button"
												className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900"
												onClick={() => setSidebarOpen(true)}
											>
												<span className="sr-only">Open Sidebar</span>
												<Bars3Icon className="h-6 w-6 text-white" aria-hidden="true" />
											</button>
										</div>
										<div>
											<a href="/">
												<Logo />
											</a>
										</div>
									</div>
								</div>
								<div className="lg:flex justify-start items-center px-8 py-4 text-primary-text hidden">
									{header !== null && (
										<>
											{hasBackArrow && (
												<div className="mr-2">
													<Link to={backTo ?? (-1 as any)}>
														<IconSvg icon="chevron-left" />
													</Link>
												</div>
											)}
											<div className="text-xl xl:text-2xl h-10 flex items-center flex-wrap gap-x-1">
												{header.split('/').length === 1 ? (
													header
												) : (
													<>
														{header
															.split('/')
															.map((item: string, index: number, arr: string[]) => {
																const isLast = index === arr.length - 1
																return (
																	<Fragment key={item}>
																		{index !== 0 && (
																			<span
																				className={isLast ? 'text-white' : ''}
																			>
																				/
																			</span>
																		)}
																		<span className={isLast ? 'text-white' : ''}>
																			{item}
																		</span>
																	</Fragment>
																)
															})}
													</>
												)}
											</div>
										</>
									)}
								</div>
								<ComponentGuard
									includeRoutes={[
										'/manage/rep-clubs/:club_id/events/:event_id',
										'/manage/rep-clubs/events/:event_id',
									]}
									excludeRoutes={[
										'/manage/rep-clubs/:club_id/events/revenue',
										'/manage/rep-clubs/:club_id/events/quick-link',
										'/manage/rep-clubs/events/revenue',
										'/manage/rep-clubs/events/quick-link',
									]}
								>
									<></>
								</ComponentGuard>
								<div className="flex gap-7 py-4 sm:px-8 px-4 items-center">
									<div className="fixed left-1/2 translate-x-[-50%]">
										<ComponentGuard
											excludeRoutes={[
												'/profile',
												'/profile/personal',
												'/profile/social',
												'/profile/location',
												'/profile/notifications',
												'/profile/information',
												'/profile/tax-configuration',
												'/profile/stripe',
												'/events',
												'/home',
												'/manage/rep-clubs',
												'/manage/rep-clubs/:club_id/*',
												'/manage/rep-clubs/create',
												'/manage/rep-clubs/:club_id/rep-points/*',
												'/offers/*',
												'/rep-clubs/:club_id/*',
												'/users/:userId',
											]}
										>
											<div className="lg:flex justify-end hidden">
												<SearchBar autoSuggestion ltr />
											</div>
										</ComponentGuard>
									</div>
									<TopBarActiveJobs />
									<div className="flex-none">
										<CreateActionDropdown />
									</div>
									<ComponentGuard excludeRoutes={['/profile/notifications']}>
										<div>
											<NotificationDropdown />
										</div>
									</ComponentGuard>
									<div className="flex-none">
										<ProfileDropdown />
									</div>
								</div>
							</div>
						</div>
					</div>
					<ComponentGuard excludeRoutes={['/events', '/offers/*']}>
						<div className="h-[48px] lg:hidden">
							<div className="sm:px-8 px-4 w-full fixed top-[72px] pb-4 bg-theme-surface z-[2]">
								<div className="flex items-center justify-between py-1">
									<div className="flex items-center w-full justify-between gap-1">
										<ComponentGuard
											excludeRoutes={[
												'/profile',
												'/profile/personal',
												'/profile/social',
												'/profile/location',
												'/profile/notifications',
												'/profile/information',
												'/profile/tax-configuration',
												'/profile/stripe',
												'/events',
												'/home',
												'/manage/rep-clubs',
												'/manage/rep-clubs/:club_id/*',
												'/manage/rep-clubs/create',
												'/manage/rep-clubs/:club_id/rep-points/*',
												'/offers/*',
												'/rep-clubs/:club_id/*',
												'/users/:userId',
											]}
										>
											<div className="flex justify-end">
												<SearchBar autoSuggestion ltr />
											</div>
										</ComponentGuard>
										<div className="flex justify-start items-center text-primary-text">
											{header !== null && (
												<>
													{hasBackArrow && (
														<div className="mr-2">
															<Link to={backTo ?? (-1 as any)}>
																<IconSvg icon="chevron-left" />
															</Link>
														</div>
													)}
													<div className="text-base sm:text-2xl flex items-center flex-wrap gap-x-1">
														{header.split('/').length === 1 ? (
															header
														) : (
															<>
																{header
																	.split('/')
																	.map(
																		(
																			item: string,
																			index: number,
																			arr: string[]
																		) => {
																			const isLast = index === arr.length - 1
																			return (
																				<Fragment key={item}>
																					{index !== 0 && (
																						<span
																							className={
																								isLast
																									? 'text-white'
																									: ''
																							}
																						>
																							/
																						</span>
																					)}
																					<span
																						className={
																							isLast ? 'text-white' : ''
																						}
																					>
																						{item}
																					</span>
																				</Fragment>
																			)
																		}
																	)}
															</>
														)}
													</div>
												</>
											)}
										</div>
										<ComponentGuard
											includeRoutes={[
												'/manage/rep-clubs/:club_id/events/:event_id',
												'/manage/rep-clubs/events/:event_id',
											]}
										>
											<></>
										</ComponentGuard>
									</div>
									{/* <a
										href="/pie-hub"
										className="rounded-3xl transition-all duration-300 bg-secondary-button w-10 h-10 hover:bg-secondary-button-hover flex justify-center items-center space-x-1 flex-shrink-0"
									>
										<IconSvg icon="information" />
									</a> */}
								</div>
							</div>
						</div>
					</ComponentGuard>

					<main className="pb-4">
						{/* Start main area*/}
						<Outlet />
						{/* End main area */}
					</main>
					<ComponentGuard
						excludeRoutes={[
							'/profile/personal',
							'/profile/social',
							'/profile/location',
							'/profile/settings',
							'/profile/tax-configuration',
							'/rep-clubs/:club_id',
							'/manage/rep-clubs',
							'/manage/rep-clubs/:club_id/*',
							'/manage/rep-clubs/create',
							'/manage/rep-clubs/events',
							'/manage/rep-clubs/events/create',
							'/manage/rep-clubs/events/:event_id',
							'/manage/rep-clubs/events/:event_id/*',
							'/manage/rep-clubs/:club_id/events/revenue',
							'/manage/rep-clubs/:club_id/events/:event_id/revenue',
							'/manage/rep-clubs/:club_id/events/quick-link',
							'/manage/rep-clubs/events/revenue',
							'/manage/rep-clubs/events/:event_id/revenue',
							'/manage/rep-clubs/events/quick-link',
							'/rep-clubs/:club_id/*',
						]}
					>
						<div className="lg:block hidden">
							<div className="min-[1440px]:w-[1440px] w-full fixed top-[72px] left-1/2 -translate-x-1/2">
								<div className="sm:pr-8 pr-4 lg:pt-4 pt-0 absolute right-0 top-0">
									<aside className="w-[200px]">
										<div className="space-y-2">
											<DidYouKnowBlock />
											<TrendingPiesBlock />
											<FooterLinks />
										</div>
									</aside>
								</div>
							</div>
						</div>
					</ComponentGuard>
				</div>
			</div>
			<UpdateTermsOfServiceAndPrivacyModal />
			<ComponentGuard includeRoutes={['/profile/information', '/profile/personal']}>
				<DeleteAccountBlock openDelete={openDeleteAccount} setOpenDelete={setOpenDeleteAccount} />
			</ComponentGuard>
			{loadingLogout && (
				<div className="flex justify-center items-center py-4 size-full fixed top-0 left-0 bg-primary-surface/[0.4] backdrop-blur-lg bg-opacity-90 z-[9999]">
					<FontAwesomeIcon icon={['fas', 'spinner']} size="2x" spin className="text-white" />
				</div>
			)}
		</div>
	)
}
